<template>
	<div width="100%" class="card">
		<div class="header-section">
			<h2>{{ t('task_mngmt.title') }}</h2>
			<div class="date-filter">
				<VueDatePicker v-model="selectedDateRange" range :placeholder="t('all_dates')" :format="'dd-MM-yyyy'"
					:enable-time-picker="false" :clearable="true" @update:model-value="onDateRangeChange"
					:preset-ranges="datePresets" :multi-calendars="2" />
			</div>
		</div>

		<div class="Container">
			<div id="tableContainer">
				<div id="taskTable" v-if="project_tasks && project_tasks.length > 0">
					<DataTable :value="project_tasks" stripedRows tableStyle="width: 40rem" :loading="loading">
						<Column field="market" :header="t('market', 2)"
							style="text-align: center; border-radius: 8px 0 0 0"></Column>
						<Column field="all_tasks" :header="t('task_mngmt.all_tasks')" style="text-align: center">
						</Column>
						<Column field="assigned" :header="t('assigned')" style="text-align: center"></Column>
						<Column field="in_progress" :header="t('in_progress')" style="text-align: center"></Column>
						<Column field="done" :header="t('done')" style="text-align: center"></Column>
						<Column field="abandoned" :header="t('abandoned')"
							style="text-align: center; border-radius: 0 8px 0 0"></Column>
					</DataTable>
				</div>
				<div id="taskTable" v-else>
					<DataTable :value="[{ market: 0, all_tasks: 0, assigned: 0, in_progress: 0, done: 0, abandoned: 0 }]"
						stripedRows tableStyle="width: 40rem" :loading="loading">
						<Column field="market" :header="t('market', 2)"
							style="text-align: center; border-radius: 8px 0 0 0"></Column>
						<Column field="all_tasks" :header="t('task_mngmt.all_tasks')" style="text-align: center">
						</Column>
						<Column field="assigned" :header="t('assigned')" style="text-align: center">
						</Column>
						<Column field="in_progress" :header="t('in_progress')" style="text-align: center">
						</Column>
						<Column field="done" :header="t('done')" style="text-align: center">
						</Column>
						<Column field="abandoned" :header="t('abandoned')"
							style="text-align: center; border-radius: 0 8px 0 0"></Column>
					</DataTable>
				</div>
			</div>
			<div id="tasksChart">
				<apexchart v-if="project_tasks_total.some(val => val > 0)" type="bar" width="500"
					:options="tasksChartOptions" :series="tasksChartOptions.series">
				</apexchart>
				<div v-else class="no-data-chart">
					<p>{{ t('no_data') }}</p>
				</div>
			</div>
		</div>

		<div class="midSection">
			<h2>{{ t('task_mngmt.teams') }}</h2>
			<div class="legend">
				<div class="legend-assigned">
					<div class="legend-circle-assigned"></div>
					<div>{{ t('assigned') }}</div>
				</div>
				<div class="legend-inprogress">
					<div class="legend-circle-inprogress"></div>
					<div>{{ t('in_progress') }}</div>
				</div>
				<div class="legend-done">
					<div class="legend-circle-done"></div>
					<div>{{ t('done') }}</div>
				</div>
				<div class="legend-abandoned">
					<div class="legend-circle-abandoned"></div>
					<div>{{ t('abandoned') }}</div>
				</div>
			</div>
		</div>

		<div class="teamsContainer">
			<div id="teamsChart" v-for="user in users" :key="user.user_id">
				<taskTeamChart :userData="user" :dateRange="selectedDateRange" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import axios from 'axios';

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import calendar from '../../components/dashboard/calendar.vue';

import taskTeamChart from './taskTeamChart.vue';

//i18n
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

import { storeToRefs } from 'pinia/dist/pinia';
import { useMarketStore } from '@/store/global/market';

const marketStore = useMarketStore();
const { selectedProject, selectedMarket } = storeToRefs(marketStore);

const project_tasks = ref();
const project_tasks_total = ref([1, 1, 1, 1]);
const users = ref();
const loading = ref(false);
const selectedDateRange = ref(null);

const datePresets = ref([
	{ label: t('dashboard.calendar.this_month'), range: [new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()] },
	{ label: t('dashboard.calendar.last_15_days'), range: [new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), new Date()] },
	{ label: t('dashboard.calendar.last_30_days'), range: [new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()] },
	{ label: t('dashboard.calendar.last_3_months'), range: [new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), new Date()] },
	{ label: t('dashboard.calendar.last_12_months'), range: [new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()] }
]);

const formatDateForAPI = (date) => {
	if (!date) return null;
	const d = new Date(date);
	return d.toISOString().split('T')[0];
};

const startDate = computed(() => {
	return selectedDateRange.value && selectedDateRange.value[0]
		? formatDateForAPI(selectedDateRange.value[0])
		: null;
});

const endDate = computed(() => {
	return selectedDateRange.value && selectedDateRange.value[1]
		? formatDateForAPI(selectedDateRange.value[1])
		: null;
});

const fetchProjectTasks = async () => {
	if (!selectedProject.value && !selectedMarket.value) return;

	loading.value = true;

	try {
		const payload = {
			market_id: selectedMarket.value,
			project_id: selectedProject.value,
		};

		if (startDate.value) payload.start_date = startDate.value;
		if (endDate.value) payload.end_date = endDate.value;

		const response = await axios.post(
			'vuejs/project_task_table',
			payload,
			{ withCredentials: true }
		);

		if (response.data.success === true) {
			project_tasks.value = response.data.data;

			if (response.data.total && Array.isArray(response.data.total)) {
				project_tasks_total.value = [...response.data.total];
			} else {
				project_tasks_total.value = [0, 0, 0, 0];
			}

			if (response.data.date_range) {
			}
		}
	} catch (error) {
		console.error('Error fetching project tasks:', error);
		project_tasks_total.value = [0, 0, 0, 0];
	} finally {
		loading.value = false;
	}
};

const fetchTeamTasks = async () => {
	if (!selectedProject.value && !selectedMarket.value) return;

	try {
		const payload = {
			market_id: selectedMarket.value,
			project_id: selectedProject.value,
		};

		if (startDate.value) payload.start_date = startDate.value;
		if (endDate.value) payload.end_date = endDate.value;

		const response = await axios.post(
			'vuejs/team_task_chart',
			payload,
			{ withCredentials: true }
		);

		if (response.data.success === true) {
			users.value = response.data.data;
		}
	} catch (error) {
		console.error('Error fetching team tasks:', error);
	}
};

const onDateRangeChange = (newRange) => {
	nextTick(() => {
		fetchProjectTasks();
		fetchTeamTasks();
	});
};

const tasksChartOptions = computed(() => ({
	chart: {
		type: 'bar',
		height: 350,
	},
	plotOptions: {
		bar: {
			distributed: true,
			borderRadius: 8,
			borderRadiusApplication: 'end',
		},
	},
	xaxis: {
		categories: [t('assigned'), t('in_progress'), t('done'), t('abandoned')],
	},
	series: [
		{
			name: 'Tasks',
			data: project_tasks_total.value.slice(1),
		},
	],
	colors: ['var(--jaune-500)', 'var(--bleu-400)', 'var(--vert-500)', 'var(--rouge-500)'],
	legend: {
		show: false,
	},
}));

watch([selectedProject, selectedMarket], () => {
	fetchProjectTasks();
	fetchTeamTasks();
}, { immediate: true });

const dashboardManagementSelectedDates = ref(localStorage.getItem('dashboardManagementSelectedDates'));
watch(
	dashboardManagementSelectedDates,
	async (newValue, oldValue) => {
		if (!selectedDateRange.value) {
			fetchTeamTasks();
		}
	},
	{ immediate: false }
);

fetchProjectTasks();
</script>

<style lang="scss" scoped>
.apexcharts-legend-text {
	padding-left: 15px;
	margin-left: -15px;
}

.card {
	border-radius: 1rem;
	padding: 1.5rem 0.5rem 1rem;
	min-width: 250px;
	flex-grow: 1;

	h2 {
		margin: 0.3rem 0 1rem;

		span {
			margin-left: 1rem;
			position: relative;
			z-index: 2;

			&::after {
				content: '';
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				width: 2.2rem;
				height: 2.2rem;
				border-radius: 50%;
				z-index: -1;
				background-size: cover;
			}

			&.gold::after {
				background-image: url('/src/assets/icon/gold-mark.svg');
			}

			&.silver::after {
				background-image: url('/src/assets/icon/silver-mark.svg');
			}

			&.cooper::after {
				background-image: url('/src/assets/icon/cooper-mark.svg');
			}
		}
	}

	.values,
	.statistics {
		gap: 0.7rem;
	}

	.values {
		justify-content: left;
		margin-bottom: 1rem;
		font-size: 3rem;
		font-weight: 600;

		& :first-child {
			color: var(--bleu-400);
		}

		& :last-child {
			font-size: 2.5rem;
		}
	}

	.statistics {
		justify-content: center;
		font-weight: 500;
		font-size: 0.8rem;
		color: var(--gris-400);

		span {
			display: inline-flex;
			margin: 5px 0 0 0;
		}

		.small {
			width: 1rem;
			margin: 0 5px 0 5px;
		}

		& :last-child {
			font-size: 0.7rem;
		}
	}
}

.header-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;

	h2 {
		margin: 0;
	}
}

.date-filter {

	:deep(.dp__input) {
		border-radius: 8px;
		border: 1px solid var(--gris-300);
		padding: .5rem 2rem;
		font-size: 0.9rem;
		min-width: 250px;

		&:focus {
			border-color: var(--bleu-400);
			outline: none;
		}
	}

	:deep(.dp__clear_icon) {
		color: var(--gris-500);
	}
}

.taskManagementContainer {
	display: flex;
	gap: 3rem;
}

.Container {
	display: flex;
	gap: 3rem;
	justify-content: space-between;
}

#tableContainer {
	height: 365px;
	display: inline-flex;
	background-color: var(--blanc);
	border-radius: 15px;
}

#taskTable {
	width: 100%;
	padding: 5rem;
}

.teamsContainer {
	display: flex;
	gap: 6rem;
	flex-wrap: wrap;
	align-items: center;
	background-color: var(--blanc);
	border-radius: 15px;
	padding: 3rem;
}

#tasksChart {
	width: 100%;
	background-color: var(--blanc);
	border-radius: 15px;
	display: flex;
	justify-content: center;
	align-items: center;
}

.colorRed {
	color: var(--rouge-500);
	fill: var(--rouge-500);
}

.colorGreen {
	color: var(--vert-500);
	fill: var(--vert-500);
}

.midSection {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 40px;
}

.legend {
	display: flex;
	gap: 1rem;
	justify-content: flex-end;
	align-items: center;
	font-size: 1rem;
	font-weight: 500;
	color: var(--gris-400);
}

.legend-assigned,
.legend-inprogress,
.legend-done,
.legend-abandoned {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.legend-circle-assigned,
.legend-circle-inprogress,
.legend-circle-done,
.legend-circle-abandoned {
	width: 1rem;
	height: 1rem;
	border-radius: 50%;
}

.legend-circle-assigned {
	background-color: var(--jaune-500);
}

.legend-circle-inprogress {
	background-color: var(--bleu-400);
}

.legend-circle-done {
	background-color: var(--vert-500);
}

.legend-circle-abandoned {
	background-color: var(--rouge-500);
}

@media screen and (max-width: 1300px) {
	.taskManagementContainer {
		flex-direction: column;
	}
}
</style>